/**
 * PricingTable — the full resolved price list for the signed-in owner
 * (GET /v1/billing/rates), rendered as one simple table: Operation ·
 * Unit · Credits, with product-area subheader rows. Discounted rows
 * show the standard price struck through.
 *
 * Shown inside the "Pricing" dialog opened from Settings › Billing ›
 * Usage & Limits (it replaced the old standalone Rate Card page).
 */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, CircularProgress, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, alpha,
} from '@mui/material';

import {
  fetchRequest as fetchRatesRequest,
  clearState as clearRates,
} from '../../redux/actions/billing/rates';
import { describeActivity, formatCredits } from './billingHelpers';

// Operation prefix -> display group. Order here = render order.
const GROUPS = [
  { prefix: 'inference.',            title: 'Inference' },
  { prefix: 'adapter_training.',     title: 'Foundation fine-tuning (predictfm)' },
  { prefix: 'external_fm_training.', title: 'Foundation fine-tuning (other families)' },
  { prefix: 'custom_training.',      title: 'Custom training (TF / PyTorch / sklearn / XGBoost)' },
  { prefix: 'discovery.',            title: 'Goals & discovery' },
  { prefix: 'hosting.',              title: 'Model hosting' },
  { prefix: 'storage.',              title: 'Storage' },
  { prefix: 'ingestion.',            title: 'Data ingestion' },
  { prefix: 'query.',                title: 'Queries' },
  { prefix: 'byom.',                 title: 'Bring your own model' },
  { prefix: 'eval.',                 title: 'Evaluation & safety' },
  { prefix: 'ask_data.',             title: 'Ask your data' },
  { prefix: 'embeddings.',           title: 'Embeddings' },
];

// Friendly per-row labels for ops describeActivity() doesn't cover well.
const OP_LABELS = {
  'inference.predictfm_ttsi':          'Foundation — predictfm (with self-improvement)',
  'inference.predictfm_no_ttsi':       'Foundation — predictfm (standard)',
  'inference.triton_shared':           'Custom model (shared)',
  'inference.byom_shared':             'BYOM (shared)',
  'inference.triton_dedicated':        'Custom model (dedicated pod)',
  'inference.byom_dedicated':          'BYOM (dedicated pod)',
  'custom_training.cpu_per_second':    'CPU compute',
  'custom_training.gpu_per_second':    'GPU compute',
  'discovery.run':                     'Discovery scan',
  'discovery.tournament':              'Model tournament',
  'discovery.live_day':                'Live goal monitoring',
  'discovery.scenario':                'What-if scenario',
  'discovery.scenario_parse':          'Scenario parsing (natural language)',
  'discovery.scenario_explain':        'Scenario explanation',
  'discovery.scenario_sensitivity':    'Sensitivity analysis',
  'embeddings.maintenance':            'Index maintenance',
  'hosting.kserve_dedicated_per_day':  'Dedicated pod',
  'hosting.modelmesh_shared_per_day':  'Shared pool',
  'hosting.foundation_shared_per_day': 'Foundation pool',
  'storage.gb_day_hot':                'Hot storage',
  'storage.gb_day_warm':               'Warm storage (30+ days old)',
  'storage.gb_day_cold':               'Cold storage (365+ days old)',
  'ingestion.bulk_per_gb':             'Bulk upload',
  'ingestion.streaming_per_thousand':  'Streaming events',
  'ingestion.connector_orchestration': 'Connector run',
  'query.per_gb_scanned':              'Data scanned',
  'byom.upload_per_mb':                'Model upload',
  'byom.validate':                     'Model validation',
  'eval.backbone_full_suite':          'Full benchmark suite',
  'eval.adapter_quick_eval':           'Quick eval',
  'eval.mia_score':                    'Privacy (MIA) score',
  'ask_data.input_per_1k_tokens':      'Input tokens',
  'ask_data.output_per_1k_tokens':     'Output tokens',
  'ask_data.query_execution':          'Query execution',
  'ask_data.session_summarise':        'Session compression',
};

// Billing unit per row, derived from the operation path.
const unitFor = (op) => {
  if (op.includes('per_1k_tokens'))      return 'per 1k tokens';
  if (op.includes('per_second'))         return 'per second';
  if (op.includes('per_mb'))             return 'per MB';
  if (op.includes('per_gb') || op.includes('bulk_per_gb')) return 'per GB';
  if (op.includes('per_thousand'))       return 'per 1k events';
  if (op.startsWith('storage.gb_day'))   return 'per GB-day';
  if (op.includes('per_day') || op === 'discovery.live_day') return 'per day';
  if (op.startsWith('inference.'))       return 'per request';
  if (op === 'embeddings.maintenance')   return 'per 100 points';
  if (op === 'ingestion.connector_orchestration') return 'per run';
  if (op.startsWith('adapter_training.')
    || op.startsWith('external_fm_training.')
    || op.startsWith('eval.'))           return 'per run';
  if (op.startsWith('discovery.scenario')) return 'per run';
  if (op === 'discovery.run' || op === 'discovery.tournament') return 'per run';
  return 'per unit';
};

const opLabel = (op) => {
  if (OP_LABELS[op]) return OP_LABELS[op];
  const described = describeActivity(op);
  if (described.category !== 'Other') return described.label;
  // Fall back to prettifying the leaf of the dotted path.
  const leaf = op.split('.').slice(1).join(' · ');
  return leaf.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const fmtRate = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (n === 0) return 'Free';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const PricingTable = () => {
  const dispatch = useDispatch();
  const { response: data, error } = useSelector((s) => s.billing_rates);
  // Clear first so a remount (dialog re-open) shows the spinner and
  // refetches instead of flashing a stale rate card.
  useEffect(() => {
    dispatch(clearRates());
    dispatch(fetchRatesRequest());
  }, [dispatch]);
  const loading = !data && !error;

  const groups = useMemo(() => {
    const rates = data?.rates || [];
    return GROUPS
      .map((g) => ({ ...g, rows: rates.filter((r) => r.operation.startsWith(g.prefix)) }))
      .filter((g) => g.rows.length > 0);
  }, [data]);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }
  if (error) {
    return <Alert severity="error">Failed to load pricing: {String(error)}</Alert>;
  }

  const creditUsd = data?.credit_value_usd;
  const overageAllowed = Boolean(data?.overage_allowed);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everything is priced in credits
        {creditUsd != null && <> — 1 credit = <strong>${Number(creditUsd).toFixed(2)}</strong></>}.
        These are your personalized rates with plan discounts applied.{' '}
        {overageAllowed
          ? <>Your plan supports pay-as-you-go past a zero balance
            (up to {formatCredits(data?.overage_floor_credits)} credits).</>
          : <>Paid operations pause at zero balance — top up or upgrade to continue.</>}
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Operation</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Credits</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g) => (
            <React.Fragment key={g.prefix}>
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                    py: 0.75,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: 10.5 }}>
                    {g.title}
                  </Typography>
                </TableCell>
              </TableRow>
              {g.rows.map((rate) => {
                const discounted = rate.effective !== rate.base;
                return (
                  <TableRow key={rate.operation} hover>
                    <TableCell>{opLabel(rate.operation)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {unitFor(rate.operation)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {discounted && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ textDecoration: 'line-through', mr: 0.75 }}
                        >
                          {fmtRate(rate.base)}
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 700, color: discounted ? 'success.main' : 'text.primary' }}
                      >
                        {fmtRate(rate.effective)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      </Box>
    </Box>
  );
};

export default PricingTable;
